// SEOチェッカー 共有型定義
// フロント / Pages Functions / DB 層で共通利用するコントラクト。
// 基準ソース: Obsidian ClaudeCode/knowledge/SEO/Google公式-AI検索最適化ガイド.md

/** チェック項目のカテゴリ（スコアリング設計と対応） */
export type Category =
  | 'technical' // テクニカル基盤 (25)
  | 'cwv' // ページ体験 Core Web Vitals (20)
  | 'content' // コンテンツ品質 LLM評価 (25)
  | 'structured' // 構造化データ (10)
  | 'media' // メディア (10)
  | 'risk'; // リスク検査 減点方式 (10)

/** 各チェック項目の判定状態 */
export type ItemStatus = 'pass' | 'warn' | 'fail' | 'na';

/** チェックジョブの実行状態 */
export type CheckStatus = 'running' | 'done' | 'error';

/** CWV/Lighthouse の値がフィールド実測かラボ値か */
export type MetricSource = 'field' | 'lab' | 'none';

/** カテゴリ別配点（合計100）。critical項目違反時はキャップ適用。 */
export const CATEGORY_WEIGHTS: Record<Category, number> = {
  technical: 25,
  cwv: 20,
  content: 25,
  structured: 10,
  media: 10,
  risk: 10,
};

export const CATEGORY_LABELS: Record<Category, string> = {
  technical: 'テクニカル基盤',
  cwv: 'ページ体験(CWV)',
  content: 'コンテンツ品質',
  structured: '構造化データ',
  media: 'メディア',
  risk: 'リスク検査',
};

/** 1つのチェック項目の結果 */
export interface CheckItemResult {
  /** 基準マスタ内の一意キー（例: 'http-200', 'https', 'cwv-lcp'） */
  key: string;
  category: Category;
  /** 項目の表示名 */
  label: string;
  status: ItemStatus;
  /**
   * 項目スコア 0〜100（カテゴリ内での達成度）。
   * na の場合は集計から除外。
   */
  score: number;
  /** この項目がカテゴリ内で占める重み（相対値） */
  weight: number;
  /** 判定根拠となった実測データ（HTTPステータス、値、抽出テキスト等） */
  evidence?: Record<string, unknown>;
  /** 改善アドバイス（Obsidian基準から引用） */
  advice?: string;
  /**
   * critical項目か（noindex誤設置・Googlebotブロック・HTTP異常）。
   * fail時は総合スコアに上限キャップを課す。
   */
  critical?: boolean;
}

/** カテゴリ別の集計スコア */
export interface CategoryScore {
  category: Category;
  label: string;
  /** 0〜100 に正規化したカテゴリスコア（available=falseの場合は0で意味を持たない） */
  score: number;
  /** 満点に対する寄与点（CATEGORY_WEIGHTS 換算後） */
  weightedPoints: number;
  maxPoints: number;
  /**
   * このカテゴリを測定できたか。false の場合（例: PSI失敗でCWV全項目na）は
   * 総合スコアの分母からこのカテゴリの配点を除外して再正規化する。
   */
  available?: boolean;
  /** available=false の場合のユーザー向け理由（例: 「サイトの読み込みが重く…」） */
  unavailableReason?: string;
}

/** 1回のチェック結果全体 */
export interface CheckResult {
  url: string;
  finalUrl?: string;
  checkedAt: string; // ISO8601
  status: CheckStatus;
  error?: string;
  /** 0〜100 の総合スコア（キャップ適用後） */
  totalScore: number;
  /** キャップ適用前の素点（参考表示用） */
  rawScore?: number;
  /** critical項目failによりキャップされたか */
  capped: boolean;
  categoryScores: CategoryScore[];
  items: CheckItemResult[];
  /** CWVの値がフィールド実測かラボかを結果全体で明示 */
  cwvSource: MetricSource;
}

/** DB: サイト */
export interface Site {
  id: string;
  name: string;
  clientName: string | null;
  createdAt: string;
}

/** DB: 登録URL */
export interface UrlEntry {
  id: string;
  siteId: string;
  url: string;
  label: string | null;
  active: boolean;
  createdAt: string;
}

/** URL一覧に付随する最新チェックのサマリ（一覧の最新スコア/前回比表示用） */
export interface LatestCheckSummary {
  totalScore: number | null;
  runAt: string;
  prevScore: number | null;
}

/** 一覧用: URLエントリに最新スコアサマリを付与した形 */
export interface UrlEntryWithLatest extends UrlEntry {
  latest: LatestCheckSummary | null;
}

/** DB: チェック履歴の1レコード（推移チャート用の軽量版） */
export interface CheckRecord {
  id: string;
  urlId: string;
  runAt: string;
  status: CheckStatus;
  totalScore: number | null;
  categoryScores: CategoryScore[] | null;
}

/** チェック実行APIのレスポンス（ジョブ即時発行） */
export interface CheckJobResponse {
  checkId: string;
  status: CheckStatus;
}

/** ジョブポーリングのレスポンス */
export interface CheckJobStatusResponse {
  checkId: string;
  status: CheckStatus;
  result?: CheckResult;
  error?: string;
}

// ── 公開版（社外リード獲得）関連 ─────────────────────────────

/** DB: リード（社外ユーザーの無料診断申込情報） */
export interface Lead {
  id: string;
  email: string;
  company: string | null;
  phone: string | null;
  targetUrl: string;
  source: string | null;
  createdAt: string;
}

/** 公開チェック申込リクエスト（フォーム送信） */
export interface PublicCheckRequest {
  url: string;
  email: string;
  /** 会社名もしくはお名前（必須。営業ツール連携の owner_name/clinic_name に使う） */
  company: string;
  phone?: string;
  /** Cloudflare Turnstile のレスポンストークン */
  turnstileToken: string;
  /** 流入元（UTM等、任意） */
  source?: string;
}

/** 公開チェック申込レスポンス（ジョブ即時発行 + leadId） */
export interface PublicCheckResponse {
  checkId: string;
  leadId: string;
  status: CheckStatus;
}

/** 公開結果ページ用レスポンス（/api/public/checks/:id） */
export interface PublicCheckStatusResponse {
  checkId: string;
  status: CheckStatus;
  result?: CheckResult;
  error?: string;
}

/** 管理画面: リード一覧の1行 */
export interface LeadListItem extends Lead {
  latestCheckId: string | null;
  latestStatus: CheckStatus | null;
  latestScore: number | null;
}

/** レート制限チェックの結果 */
export interface RateLimitCheckResult {
  allowed: boolean;
  reason?: 'ip_daily_limit' | 'global_daily_limit' | 'invalid_url' | 'turnstile_failed';
  message?: string;
}
